'use client';
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
/**
 * TLZ-561 (A2). The consent prompt. It fills the panel because agreeing to share a
 * screen is not a decision to make out of the corner of an eye, and it says who is
 * watching, what can be shared, and how long the offer stands.
 */
const SURFACE_LABELS = {
    browser: 'A browser tab',
    window: 'An app window',
    monitor: 'Your entire screen',
};
function secondsRemaining(expiresAt) {
    return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
}
export function ConsentOverlay({ agentName, surfaces, timeoutSeconds, expiresAt, capturing = false, onAccept, onDecline, }) {
    const who = (agentName === null || agentName === void 0 ? void 0 : agentName.trim()) ? agentName.trim() : 'The assistant';
    const panelRef = useRef(null);
    const [remaining, setRemaining] = useState(() => secondsRemaining(expiresAt));
    useEffect(() => {
        setRemaining(secondsRemaining(expiresAt));
        const interval = setInterval(() => setRemaining(secondsRemaining(expiresAt)), 250);
        return () => clearInterval(interval);
    }, [expiresAt]);
    // Focus moves into the prompt so it can be answered, and dismissed, from the keyboard.
    useEffect(() => {
        var _a;
        (_a = panelRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            // While the browser's own picker is open the answer is no longer ours to give:
            // the request has been accepted and only the picker can decide what happens next.
            if (capturing) {
                return;
            }
            event.stopPropagation();
            onDecline();
            return;
        }
        // aria-modal is a promise that focus stays inside; Tab is where that promise is
        // kept or broken.
        if (event.key !== 'Tab') {
            return;
        }
        const panel = panelRef.current;
        if (!panel) {
            return;
        }
        const focusable = Array.from(panel.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'));
        if (focusable.length === 0) {
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || active === panel)) {
            event.preventDefault();
            last.focus();
        }
        else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    }, [capturing, onDecline]);
    return (_jsxs("div", { ref: panelRef, role: "dialog", "aria-modal": "true", "aria-labelledby": "screenshare-consent-title", "aria-describedby": "screenshare-consent-viewers", tabIndex: -1, onKeyDown: handleKeyDown, className: cn('bg-background/98 absolute inset-0 z-30 flex flex-col justify-center gap-3 rounded-[28px] p-5 outline-none'), children: [_jsxs("h2", { id: "screenshare-consent-title", className: "text-fg1 text-base font-semibold", children: [who, " would like to see your screen"] }), _jsx("p", { className: "text-fg1/80 text-sm", children: "You choose what to share:" }), _jsx("ul", { className: "text-fg1/80 list-disc space-y-1 pl-5 text-sm", children: surfaces.map((surface) => (_jsx("li", { children: SURFACE_LABELS[surface] }, surface))) }), _jsx("p", { id: "screenshare-consent-viewers", className: "text-fg1/60 text-xs", children: "The AI agent is the only viewer \u2014 no person sees your screen, and you can stop sharing at any time." }), _jsxs("div", { className: "flex flex-row gap-2 pt-1", children: [_jsx(Button, { variant: "primary", size: "sm", onClick: onAccept, disabled: capturing, children: capturing ? 'Choose what to share…' : 'Share' }), _jsx(Button, { variant: "outline", size: "sm", onClick: onDecline, disabled: capturing, children: "Not now" })] }), _jsx("p", { "aria-live": "polite", className: "text-fg1/60 text-xs", children: capturing
                    ? 'Pick a tab, window or screen in your browser’s prompt.'
                    : `This request expires in ${remaining}s.` }), _jsx("span", { className: "sr-only", children: `The request stands for ${timeoutSeconds} seconds.` })] }));
}
export default ConsentOverlay;
